import type { Event } from '@bugsnag/js';

/**
 * Fingerprints for the two documented sources of the crawler "Rejected" noise:
 * Google's Web Rendering Service shim (`wrsParams.serviceWorkers…`) and the
 * `@lcdp/offline-plugin` runtime that re-rejects the failed registration.
 * Deliberately narrow — a generic `serviceworker`/`sw.js` match would suppress
 * genuine `Error('Rejected')` failures from unrelated service-worker code.
 */
const SERVICE_WORKER_SIGNATURE = /wrsparams|offline-plugin/i;

/**
 * Detect the benign, unpreventable "Rejected" unhandled promise rejection that
 * floods Bugsnag from web crawlers.
 *
 * Google's Web Rendering Service (Googlebot, Google-Read-Aloud, …) stubs
 * `navigator.serviceWorker.register` so it rejects with a bare
 * `Error('Rejected')`. `@lcdp/offline-plugin`'s runtime re-rejects that into a
 * detached promise (`registration.then(…).catch(() => Promise.reject(err))`)
 * which nothing holds a reference to, so it surfaces as an unhandled rejection
 * we cannot catch at the `install()` call site (see `src/js/main.ts`).
 *
 * The service worker is a progressive enhancement (offline caching); failing to
 * register it in a crawler sandbox has no effect on real users, so these
 * reports are pure noise and safe to drop.
 *
 * @param event - The {@link Event} Bugsnag is about to report.
 * @returns `true` when the event is this crawler noise, so callers can cancel
 *   the report by returning `false` from `onError`.
 */
export default function isServiceWorkerRegistrationNoise(event: Event): boolean {
	if (!event.unhandled) {
		return false;
	}

	const [firstError] = event.errors;

	if (
		firstError?.errorClass !== 'Error'
		|| firstError.errorMessage !== 'Rejected'
	) {
		return false;
	}

	const framesText = (firstError.stacktrace ?? [])
		.map((frame) => `${frame.method ?? ''} ${frame.file ?? ''}`)
		.join(' ');
	const originalStack = event.originalError instanceof Error
		? (event.originalError.stack ?? '')
		: '';

	return SERVICE_WORKER_SIGNATURE.test(`${framesText} ${originalStack}`);
}
